import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMount from '../../../common/hooks/useMount';
import Icons from '../../../common/widgets/Icons';
import { SmartNotificationRepeatTypes } from '../enums';
import { resources } from '../resources/resources';
import { smartNotificationService } from '../services/smartNotifications';

interface SmartNotificationsProps {
  productId: number | null;
  categoryId: number | null;
  isForHome: boolean;
}

const contentMaxHeight = 66;

export const SmartNotifications = (props: SmartNotificationsProps) => {
  const { productId, categoryId, isForHome } = props;
  const [smartNotifications, setSmartNotifications] = useState<
    SmartNotificationProps[]
  >([]);

  const loadSmartNotifications = useCallback(
    async (abortController?: AbortController) => {
      const resp = await smartNotificationService.getSmartNotifications(
        productId,
        categoryId,
        isForHome,
        { signal: abortController?.signal },
      );
      if (!resp.hasErrors) {
        setSmartNotifications(resp.data);
      }
    },
    [productId, categoryId, isForHome],
  );

  useEffect(() => {
    const abortController = new AbortController();

    setSmartNotifications([]);

    loadSmartNotifications(abortController);

    return () => {
      abortController.abort();
    };
  }, [categoryId, productId, isForHome, loadSmartNotifications]);

  if (!smartNotifications.length) return null;
  return (
    <>
      {smartNotifications.map(e => (
        <React.Fragment key={e.id}>
          <SmartNotification {...e} />
        </React.Fragment>
      ))}
    </>
  );
};

interface SmartNotificationProps {
  id: number;
  title: string;
  description: string;
  delayMs: number;
  timeoutMs: number;
  repeatType: SmartNotificationRepeatTypes;
  days: number;
}

const SmartNotification = (props: SmartNotificationProps) => {
  const { title, description, delayMs, timeoutMs } = props;
  const { t: translate } = useTranslation();
  const [isVisible, setIsVisible] = useState<boolean>(delayMs <= 0);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const contentRef = useRef<any>(null);

  const hideNotification = () => setIsVisible(false);
  const showNotification = () => setIsVisible(true);

  const setContentHeight = () => {
    if (contentRef?.current) {
      const rect = contentRef.current.getBoundingClientRect();
      if (rect.height > contentMaxHeight) {
        contentRef.current.style.maxHeight = `${contentMaxHeight}px`;
        contentRef.current.style.overflow = 'hidden';
        setShowMore(true);
        setIsExpanded(false);
      }
    }
  };

  const onShowMoreClick = () => {
    if (contentRef?.current) {
      contentRef.current.style.maxHeight = null;
      contentRef.current.style.overflow = null;
      setShowMore(false);
      setIsExpanded(true);
    }
  };

  useMount(() => {
    let hideNotificationTimeoutId: any;
    if (timeoutMs > 0) {
      hideNotificationTimeoutId = setTimeout(hideNotification, timeoutMs);
    }
    let showNotificationTimeoutId: any;
    if (delayMs > 0) {
      showNotificationTimeoutId = setTimeout(showNotification, delayMs);
    } else {
      setContentHeight();
    }
    return () => {
      if (hideNotificationTimeoutId) clearTimeout(hideNotificationTimeoutId);
      if (showNotificationTimeoutId) clearTimeout(showNotificationTimeoutId);
    };
  });

  useEffect(() => {
    if (isVisible) {
      setContentHeight();
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className={`toast t-smart ${!isExpanded ? 't-smart-collapsed' : ''}`}>
      <div className="toast-icon">
        <Icons.ToastSmart />
      </div>
      <div className="toast-content">
        <h3 dangerouslySetInnerHTML={{ __html: _.unescape(title) }} />
        <div
          className="toast-smart-text"
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: _.unescape(description) }}
        />
        <div className="show-more">
          {showMore && (
            <a onClick={onShowMoreClick}>
              <span className="btn-text">{translate(resources.showMore)}</span>
            </a>
          )}
        </div>
      </div>
      <div className="toast-close">
        <button onClick={hideNotification}>
          <Icons.NotificationsClose />
        </button>
      </div>
    </div>
  );
};
